import { useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { Currency } from 'components/Number';
import PropTypes from 'prop-types';

import { forecastStartMonth, formatMonthLabel } from './cashflowFeeExposureHelpers';
import { buildFeeRows } from './feeCalculation';

function FeeCalculationTable({ customers, months, method }) {
  const forecastMonth = forecastStartMonth();
  const monthKeys = useMemo(
    () => (months || []).map((month) => month.month || month),
    [months]
  );
  const rows = useMemo(
    () => buildFeeRows(customers, monthKeys, method),
    [customers, monthKeys, method]
  );

  if (!monthKeys.length) return null;

  return (
    <Box overflowX="auto">
      <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
        <thead>
          <tr>
            <th style={stickyHeader} />
            {monthKeys.map((month) => (
              <th
                key={`${month}-status`}
                style={{
                  ...monthHeader,
                  background: month < forecastMonth ? '#70AD47' : '#2E75B6',
                  borderLeft: month === forecastMonth ? '2px solid #422AFB' : undefined,
                }}
              >
                {month < forecastMonth ? 'Act.' : 'Proj.'}
              </th>
            ))}
          </tr>
          <tr>
            <th style={stickyHeader}>Fee</th>
            {monthKeys.map((month) => (
              <th
                key={month}
                style={{
                  ...monthHeader,
                  background: month < forecastMonth ? '#70AD47' : '#2E75B6',
                  borderLeft: month === forecastMonth ? '2px solid #422AFB' : undefined,
                }}
              >
                {formatMonthLabel(month)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const total = row.id === 'total';
            const totalDivider = total ? '2px solid #1F4E79' : undefined;
            return (
              <tr key={row.id} style={{ background: total ? '#F4F7FE' : undefined }}>
                <td
                  style={{
                    ...stickyCell,
                    background: total ? '#F4F7FE' : '#fff',
                    fontWeight: total ? 700 : 500,
                    borderTop: totalDivider,
                  }}
                >
                  {row.label}
                </td>
                {monthKeys.map((month, index) => (
                  <td
                    key={`${row.id}-${month}`}
                    style={{
                      padding: '8px 12px',
                      textAlign: 'right',
                      fontWeight: total ? 700 : 400,
                      borderTop: totalDivider,
                      borderLeft: month === forecastMonth ? '2px solid #422AFB' : undefined,
                    }}
                  >
                    {row.values[index] != null ? (
                      <Currency value={row.values[index]} maximumFractionDigits={0} />
                    ) : '—'}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

const stickyHeader = {
  position: 'sticky',
  left: 0,
  background: '#fff',
  textAlign: 'left',
  padding: '8px 12px',
  zIndex: 1,
};

const stickyCell = {
  position: 'sticky',
  left: 0,
  padding: '8px 12px',
  zIndex: 1,
};

const monthHeader = {
  padding: '8px 12px',
  textAlign: 'right',
  color: '#fff',
  fontWeight: 700,
};

FeeCalculationTable.propTypes = {
  customers: PropTypes.arrayOf(PropTypes.object).isRequired,
  months: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  method: PropTypes.oneOf(['earned', 'end', 'prorata']).isRequired,
};

export default FeeCalculationTable;
